/**
 * The Missile Warning classification reference table (RB §5.3.5) — decision M4-D2.
 *
 * **What lives here is the fallback, not the table.** The rows that matter are
 * scenario content: an Indo-Pacific scenario models DF-17 against the rest of the
 * PRC ballistic and hypersonic inventory, a different theatre models something
 * else, and a demo models invented types. `WorldState.mwCatalogue` carries whatever
 * the scenario authored; this module carries seven generic rows — one per category —
 * for a scenario that authored none.
 *
 * Two consequences of that split, both easy to get wrong:
 *
 *  - **A scenario's catalogue is hashed with the scenario, not with the rules
 *    bundle.** Editing a scenario's models does not require a `CONTENT_VERSION` bump
 *    and does not trip §10.1's mid-exercise guard. Editing `DEFAULT_MW_CATALOGUE`
 *    does, because it is in the content bundle like every other data table.
 *  - **The catalogue is a general intelligence product, not an order of battle.** It
 *    must not be generated from the models a scenario actually fields: a table
 *    listing exactly what the enemy owns tells the other team what the enemy owns.
 *    `validateScenarioMwCatalogue` warns when it looks generated.
 *
 * `DEFAULT_MW_CATALOGUE`'s observables are transcribed from RB §5.3.5's own example
 * table. They are deliberately *unambiguous* — one row per category, so every
 * category resolves at stage 1 — because a fallback table exists to keep a scenario
 * playable, not to pose a puzzle. Posing the puzzle is the scenario author's job.
 */
import { asMwModelId } from '../model/ids.js';
/** The generic row for a category, used when a launcher names no model. */
export function genericModelIdFor(category) {
    return asMwModelId(`${category}_GENERIC`);
}
function generic(category, displayName, observables) {
    return { id: genericModelIdFor(category), displayName, category, observables };
}
/**
 * RB §5.3.5's example table, one row per category.
 *
 * Changing a value here changes `interceptModifierFor` for every scenario that
 * authored no catalogue, including the golden game — which is the whole reason WP1
 * chose these to reproduce M3's behaviour exactly.
 */
export const DEFAULT_MW_CATALOGUE = Object.freeze([
    generic('SRBM', 'Short-range ballistic missile', {
        irIntensity: 'LOW',
        boostDurationS: [40, 60],
        stagingEvents: 0,
        apogee: '100–150 km',
        groundTrack: 'Ballistic, single azimuth',
        terminal: 'Single reentry vehicle, ballistic',
        apogeeKm: [100, 150],
        rv: [1, 1],
    }),
    generic('IRBM', 'Intermediate-range ballistic missile', {
        irIntensity: 'MEDIUM',
        boostDurationS: [100, 130],
        stagingEvents: 1,
        apogee: '300–600 km',
        groundTrack: 'Ballistic, single azimuth',
        terminal: 'One to three reentry vehicles, ballistic',
        apogeeKm: [300, 600],
        rv: [1, 3],
    }),
    generic('HGV', 'Hypersonic glide vehicle', {
        irIntensity: 'MEDIUM',
        boostDurationS: [100, 130],
        stagingEvents: 1,
        apogee: 'Below 100 km, depressed',
        groundTrack: 'Azimuth changes after boost',
        terminal: 'Continues gliding',
        apogeeKm: [0, 100],
        // The glide vehicle is itself the body that arrives; it releases nothing.
        rv: [1, 1],
    }),
    generic('DA_ASAT', 'Direct-ascent anti-satellite missile', {
        irIntensity: 'MEDIUM',
        boostDurationS: [60, 90],
        stagingEvents: 1,
        apogee: 'Exoatmospheric',
        groundTrack: 'No ground track',
        terminal: 'Closes on an object in orbit',
        // Not ballistic, so there is no apogee to band — which is the tell.
        apogeeKm: null,
        rv: [0, 0],
    }),
    generic('ICBM', 'Intercontinental ballistic missile', {
        irIntensity: 'HIGH',
        boostDurationS: [180, 300],
        stagingEvents: 2,
        apogee: '1000–1300 km',
        groundTrack: 'Ballistic, single azimuth',
        terminal: 'Multiple reentry vehicles with penetration aids',
        apogeeKm: [1000, 1300],
        // "Multiple", banded so that it clears the IRBM row's ceiling of three.
        rv: [3, 10],
    }),
    generic('SPACE_LAUNCH', 'Space launch vehicle', {
        irIntensity: 'HIGH',
        boostDurationS: [400, 600],
        stagingEvents: 3,
        apogee: 'Rises continuously to orbital altitude',
        groundTrack: 'Downrange, no reentry',
        terminal: 'Orbital insertion; payload deployment',
        apogeeKm: null,
        rv: [0, 0],
    }),
    generic('TEST_OR_EXERCISE', 'Test or exercise launch', {
        irIntensity: 'MEDIUM',
        boostDurationS: [100, 130],
        stagingEvents: 1,
        apogee: 'Matches the type being tested',
        groundTrack: 'Terminates in own territory or a declared range',
        terminal: 'Matches the type being tested',
        // Deliberately non-discriminating in both columns: a test launch reads as
        // whatever it is testing, and a band that narrowed it would be a lie.
        apogeeKm: null,
        rv: [0, 10],
    }),
    // INTERCEPTOR has no row. `MwCategory` accepts one because `trueType` is assigned
    // from a launcher's `weaponType`, but nothing in the engine creates a warning
    // event for an interception, and a scenario that wants one authors the row.
]);
/**
 * The catalogue in force, with the fallback joined in.
 *
 * A scenario's catalogue **supersedes** a generic row of the same id and is
 * otherwise merged, so an author can extend the defaults with two models rather
 * than restating seven. Sorted by id, because the result is walked by
 * `pickOmittedField`-adjacent code and by the projection, and neither may depend on
 * authoring order (§7.4).
 */
export function catalogueOf(scenarioCatalogue) {
    const byId = new Map();
    for (const model of DEFAULT_MW_CATALOGUE)
        byId.set(model.id, model);
    for (const model of scenarioCatalogue)
        byId.set(model.id, model);
    return [...byId.values()].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}
export function findMwModel(catalogue, id) {
    return catalogue.find((m) => m.id === id) ?? DEFAULT_MW_CATALOGUE.find((m) => m.id === id);
}
/**
 * The category a released identity names.
 *
 * `null` for an unknown id, which the caller must treat as "not the category you
 * were asking about" rather than as a match — an identity nobody can resolve is not
 * a correct call.
 */
export function mwCategoryOf(catalogue, id) {
    return findMwModel(catalogue, id)?.category ?? null;
}
export function modelsInCategory(catalogue, category) {
    return catalogue.filter((m) => m.category === category);
}
// ---------------------------------------------------------------------------
// Warning credit (RB §5.3.6) — M5 Q12
// ---------------------------------------------------------------------------
/**
 * What the Missile Warning cell earns for calling a track at a given stage.
 *
 * **Not `RELEASE_STAGE_MODIFIER`, which it happens to equal today.** That one is
 * Joint Fires' interception bonus and belongs to the firing solution; this one is
 * the warning cell's own credit for having decided early, and the two are free to
 * diverge the moment somebody scores an exercise differently. RB §5.3.6 prices
 * waiting once and both quantities read that price, which is why they start equal
 * and why they are still two tables.
 *
 * Stage `0` — a track exists and stage 1 has not fired — is worth nothing, because
 * nothing has been decided yet.
 */
export const MW_STAGE_CREDIT = Object.freeze({
    1: 4,
    2: 3,
    3: 2,
    4: 0,
});
/**
 * The credit a release at this stage earns — **the stage term alone**.
 *
 * There is deliberately no correctness term here, for the same reason
 * `releaseStageModifierFor` withholds one from Joint Fires (decision M4-D7): being
 * right is graded against the launch's *true* model, which the seat does not know
 * and must not be told before the turn resolves. The MW rail therefore shows this
 * number and never a total, and the misclassification penalty lands in the AAR.
 */
export function mwStageCredit(stage) {
    return stage === 0 ? 0 : MW_STAGE_CREDIT[stage];
}
export const MW_EVENT_TABLE_MANIFEST = {
    DEFAULT_MW_CATALOGUE,
    MW_STAGE_CREDIT,
};
//# sourceMappingURL=mw-events.js.map